import React, { forwardRef } from "react";
import { Button as ShadcnButton } from "@/lib/ui/button";
import { cn } from "@/lib/utils";
import type { ButtonVariant, Size } from "@/types";
import Spinner from "./Spinner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: Size;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

// ---------------------------------------------------------------------------
// Variant / size mapping to shadcn primitives
// ---------------------------------------------------------------------------

type ShadcnVariant =
  | "default"
  | "brand"
  | "secondary"
  | "destructive"
  | "ghost"
  | "outline";
type ShadcnSize = "sm" | "default" | "lg";

const VARIANT_MAP: Record<ButtonVariant, ShadcnVariant> = {
  primary: "default",
  brand: "brand",
  secondary: "secondary",
  danger: "destructive",
  ghost: "ghost",
  outline: "outline",
};

const SIZE_MAP: Record<Size, ShadcnSize> = {
  sm: "sm",
  md: "default",
  lg: "lg",
};

const SPINNER_SIZE_MAP: Record<Size, Size> = {
  sm: "sm",
  md: "sm",
  lg: "md",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <ShadcnButton
        ref={ref}
        type={type}
        variant={VARIANT_MAP[variant]}
        size={SIZE_MAP[size]}
        disabled={isDisabled}
        aria-busy={isLoading}
        className={cn(
          // Restore the active scale that our design uses
          "active:scale-[0.98] rounded-lg",
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      >
        {/* Loading spinner replaces the left icon */}
        {isLoading ? (
          <Spinner
            size={SPINNER_SIZE_MAP[size]}
            className={
              variant === "primary" || variant === "danger"
                ? "text-white/80"
                : variant === "brand"
                  ? "text-primary-foreground/80"
                  : "text-current"
            }
          />
        ) : leftIcon ? (
          <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4">{leftIcon}</span>
        ) : null}

        {/* Label */}
        {children && (
          <span className={cn(isLoading && "opacity-70")}>{children}</span>
        )}

        {/* Right icon — hidden while loading */}
        {rightIcon && !isLoading && (
          <span className="shrink-0 [&>svg]:w-4 [&>svg]:h-4">{rightIcon}</span>
        )}
      </ShadcnButton>
    );
  },
);

Button.displayName = "Button";

export default Button;
